import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaHome, FaBox, FaShoppingCart, FaWallet, FaHeadset, FaUser, FaCog, FaSignOutAlt, FaTruck, FaMapMarkedAlt, FaMoneyBillWave, FaCrown, FaShieldAlt, FaFileInvoice, FaBell, FaUpload, FaTimes, FaComments, FaChartLine, FaHeart, FaStar, FaUndo, FaGift, FaChartBar, FaExternalLinkAlt, FaServer, FaUsers, FaDatabase } from 'react-icons/fa';
import './Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleLinkClick = () => {
    // Close sidebar on mobile when a link is clicked
    if (window.innerWidth <= 768) {
      onClose();
    }
  };

  const sellerMenuItems = [
    { path: '/', icon: <FaHome />, label: 'Home' },
    { path: '/analytics', icon: <FaChartLine />, label: 'Analytics' },
    { path: '/products', icon: <FaBox />, label: 'My Catalog' },
    { path: '/bulk-upload', icon: <FaUpload />, label: 'Bulk Upload' },
    { path: '/knowledge-base', icon: <FaDatabase />, label: 'Knowledge Base' },
    { path: '/orders', icon: <FaShoppingCart />, label: 'Orders' },
    { path: '/invoices', icon: <FaFileInvoice />, label: 'Invoices' },
    { path: '/finance', icon: <FaWallet />, label: 'Finance (Capital)' },
    { path: '/chat', icon: <FaComments />, label: 'Messages' },
    { path: '/subscription', icon: <FaCrown />, label: 'Subscription' },
    { path: '/support', icon: <FaHeadset />, label: 'Support' },
    { path: '/notification-settings', icon: <FaBell />, label: 'Notifications' },
    { path: '/account-settings', icon: <FaShieldAlt />, label: 'Account & Security' },
    { path: '/privacy-settings', icon: <FaCog />, label: 'Privacy Settings' }
  ];

  // Add public profile link only if user ID is available
  const userId = user?._id || user?.id;
  if (userId) {
    sellerMenuItems.push({ 
      path: `/public-profile/${userId}`, 
      icon: <FaExternalLinkAlt />, 
      label: 'Public Profile', 
      external: true 
    });
  }

  const buyerMenuItems = [
    { path: '/', icon: <FaHome />, label: 'Home' },
    { path: '/analytics', icon: <FaChartLine />, label: 'My Analytics' },
    { path: '/products', icon: <FaBox />, label: 'Shop Products' },
    { path: '/wishlist', icon: <FaHeart />, label: 'Wishlist' },
    { path: '/loyalty', icon: <FaGift />, label: 'Loyalty Rewards' },
    { path: '/orders', icon: <FaShoppingCart />, label: 'My Orders' },
    { path: '/returns', icon: <FaUndo />, label: 'Returns & Exchanges' },
    { path: '/invoices', icon: <FaFileInvoice />, label: 'Invoices' },
    { path: '/chat', icon: <FaComments />, label: 'Messages' },
    { path: '/subscription', icon: <FaCrown />, label: 'Subscription' },
    { path: '/support', icon: <FaHeadset />, label: 'Support' },
    { path: '/notification-settings', icon: <FaBell />, label: 'Notifications' },
    { path: '/account-settings', icon: <FaShieldAlt />, label: 'Account & Security' },
    { path: '/privacy-settings', icon: <FaCog />, label: 'Privacy Settings' }
  ];

  // Add public profile link only if user ID is available
  if (userId) {
    buyerMenuItems.push({ 
      path: `/public-profile/${userId}`, 
      icon: <FaExternalLinkAlt />, 
      label: 'Public Profile', 
      external: true 
    });
  }

  const supportMenuItems = [
    { path: '/backend-dashboard', icon: <FaHome />, label: 'Backend Dashboard' },
    { path: '/monitoring', icon: <FaChartBar />, label: 'Monitoring & Analytics' },
    { path: '/user-engagement', icon: <FaUsers />, label: 'User Engagement' },
    { path: '/support-dashboard', icon: <FaHeadset />, label: 'Support Tickets' },
    { path: '/api-logs', icon: <FaServer />, label: 'API Logs' },
    { path: '/account-settings', icon: <FaShieldAlt />, label: 'Account & Security' },
    { path: '/privacy-settings', icon: <FaCog />, label: 'Privacy Settings' }
  ];

  // Add public profile link only if user ID is available
  if (userId) {
    supportMenuItems.push({ 
      path: `/public-profile/${userId}`, 
      icon: <FaExternalLinkAlt />, 
      label: 'Public Profile', 
      external: true 
    });
  }

  const deliveryPartnerMenuItems = [
    { path: '/delivery-partner-dashboard', icon: <FaHome />, label: 'Dashboard' },
    { path: '/support', icon: <FaHeadset />, label: 'Support' },
    { path: '/account-settings', icon: <FaShieldAlt />, label: 'Account & Security' },
    { path: '/privacy-settings', icon: <FaCog />, label: 'Privacy Settings' }
  ];

  // Add public profile link only if user ID is available
  if (userId) {
    deliveryPartnerMenuItems.push({ 
      path: `/public-profile/${userId}`, 
      icon: <FaExternalLinkAlt />, 
      label: 'Public Profile', 
      external: true 
    });
  }

  // Determine menu items based on user type
  let menuItems = sellerMenuItems;
  if (user?.userType === 'buyer') {
    menuItems = buyerMenuItems;
  } else if (user?.userType === 'support' || user?.userType === 'admin') {
    menuItems = supportMenuItems;
  } else if (user?.userType === 'delivery_partner') {
    menuItems = deliveryPartnerMenuItems;
  }

  return (
    <div className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-header-content">
          <div>
            <h2>Cixio</h2>
            <p className="sidebar-subtitle">Software Solutions Platform</p>
          </div>
          <button className="sidebar-close-btn" onClick={onClose} aria-label="Close sidebar">
            <FaTimes />
          </button>
        </div>
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          item.external ? (
            <a
              key={item.path}
              href={item.path}
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-item sidebar-item-external"
              onClick={handleLinkClick}
            >
              <span className="sidebar-icon">{item.icon}</span>
              <span className="sidebar-label">{item.label}</span>
            </a>
          ) : (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={handleLinkClick}
            >
              <span className="sidebar-icon">{item.icon}</span>
              <span className="sidebar-label">{item.label}</span>
            </Link>
          )
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <FaUser className="user-icon" />
          <div>
            <div className="user-name">{user?.businessName || user?.phone}</div>
            <div className="user-email">{user?.email}</div>
            <div className="user-type">{user?.userType}</div>
          </div>
        </div>
        <button className="btn-logout" onClick={logout}>
          <FaSignOutAlt /> Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
